import { Decimal } from 'decimal.js';
import { addDays, addWeeks, addMonths, setDate } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
// Default timezone for Libya
const DEFAULT_TIMEZONE = 'Africa/Tripoli';
// Helper function to round to 2 decimal places using Decimal.js
function roundToTwoDecimals(value) {
    return new Decimal(value).toDecimalPlaces(2).toNumber();
}
// Calculate the remaining balance after deposit using Decimal.js for precision
function calculateRemainingBalance(params) {
    const total = new Decimal(params.total_price);
    const deposit = new Decimal(params.deposit);
    const adminFees = new Decimal(params.admin_fee_total);
    return total.minus(deposit).plus(adminFees).toDecimalPlaces(2).toNumber();
}
// Calculate individual installment amount with proper rounding
function calculateInstallmentAmount(params) {
    const remainingBalance = new Decimal(calculateRemainingBalance(params));
    const installmentCount = new Decimal(params.installment_count);
    return remainingBalance.dividedBy(installmentCount).toDecimalPlaces(2).toNumber();
}
// Calculate the next due date based on schedule type and start date with timezone support
function calculateNextDueDate(startDate, scheduleType, dueDay, installmentNumber, timezone = DEFAULT_TIMEZONE) {
    // Convert start date to shop timezone for calculations
    const zonedStartDate = fromZonedTime(startDate, timezone);
    let nextDate = new Date(zonedStartDate);
    switch (scheduleType) {
        case 'weekly':
            nextDate = addWeeks(zonedStartDate, installmentNumber);
            break;
        case 'bi-weekly':
            nextDate = addWeeks(zonedStartDate, installmentNumber * 2);
            break;
        case 'monthly':
            // Set to the specified due day of the month
            nextDate = addMonths(zonedStartDate, installmentNumber);
            nextDate = setDate(nextDate, dueDay);
            // Handle edge cases where the due day doesn't exist in the target month
            const targetMonth = nextDate.getMonth();
            const maxDaysInMonth = new Date(nextDate.getFullYear(), targetMonth + 1, 0).getDate();
            if (dueDay > maxDaysInMonth) {
                nextDate = setDate(nextDate, maxDaysInMonth);
            }
            break;
        default:
            throw new Error(`Invalid schedule type: ${scheduleType}`);
    }
    // Convert back to UTC for storage
    return toZonedTime(nextDate, timezone);
}
// Calculate penalty for overdue installments
function calculatePenalty(installment, penaltyRuleJson, currentDate = new Date(), timezone = DEFAULT_TIMEZONE) {
    try {
        const penaltyRule = JSON.parse(penaltyRuleJson);
        // Simple penalty rule v1: fixed amount per day overdue
        if (penaltyRule.type === 'fixed_per_day' && penaltyRule.amount) {
            const zonedCurrentDate = fromZonedTime(currentDate, timezone);
            const zonedDueDate = fromZonedTime(installment.due_date, timezone);
            const daysOverdue = Math.max(0, Math.floor((zonedCurrentDate.getTime() - zonedDueDate.getTime()) / (1000 * 60 * 60 * 24)));
            return roundToTwoDecimals(daysOverdue * penaltyRule.amount);
        }
        // Percentage penalty rule
        if (penaltyRule.type === 'percentage' && penaltyRule.percentage) {
            const zonedCurrentDate = fromZonedTime(currentDate, timezone);
            const zonedDueDate = fromZonedTime(installment.due_date, timezone);
            const daysOverdue = Math.max(0, Math.floor((zonedCurrentDate.getTime() - zonedDueDate.getTime()) / (1000 * 60 * 60 * 24)));
            if (daysOverdue > 0) {
                const penaltyAmount = (installment.amount_due * penaltyRule.percentage) / 100;
                return roundToTwoDecimals(penaltyAmount);
            }
        }
        return 0;
    }
    catch (error) {
        console.error('Error parsing penalty rule:', error);
        return 0;
    }
}
// Determine installment status with timezone-aware grace period calculation
function determineInstallmentStatus(installment, graceDays, currentDate = new Date(), timezone = DEFAULT_TIMEZONE) {
    if (installment.amount_paid >= installment.amount_due) {
        return 'paid';
    }
    if (installment.amount_paid > 0) {
        return 'partial';
    }
    // Calculate grace period in shop timezone
    const zonedDueDate = fromZonedTime(installment.due_date, timezone);
    const graceDate = addDays(zonedDueDate, graceDays);
    const zonedCurrentDate = fromZonedTime(currentDate, timezone);
    if (zonedCurrentDate > graceDate) {
        return 'overdue';
    }
    return 'pending';
}
// Main function to generate installment schedule - ENHANCED VERSION
export function generateInstallments(params, currentDate = new Date()) {
    // Validate input parameters
    if (params.total_price <= 0) {
        throw new Error('Total price must be positive');
    }
    if (params.deposit < 0 || params.deposit > params.total_price) {
        throw new Error('Deposit must be between 0 and total price');
    }
    if (params.installment_count < 1 || params.installment_count > 120) {
        throw new Error('Installment count must be between 1 and 120');
    }
    if (params.due_day < 1 || params.due_day > 31) {
        throw new Error('Due day must be between 1 and 31');
    }
    if (params.grace_days < 0 || params.grace_days > 30) {
        throw new Error('Grace days must be between 0 and 30');
    }
    const timezone = params.timezone || DEFAULT_TIMEZONE;
    const penaltyRule = params.penalty_rule || DEFAULT_PENALTY_RULE_JSON;
    // Calculate base installment amount using Decimal.js for precision
    const baseInstallmentAmount = calculateInstallmentAmount(params);
    const remainingBalance = calculateRemainingBalance(params);
    // Generate installments with deterministic rounding
    const installments = [];
    let totalAmount = new Decimal(0);
    let overdueCount = 0;
    let paidCount = 0;
    let nextDueDate;
    for (let i = 1; i <= params.installment_count; i++) {
        const dueDate = calculateNextDueDate(params.start_date, params.schedule_type, params.due_day, i, timezone);
        // Calculate installment amount (last installment gets the remainder to avoid rounding errors)
        let installmentAmount = baseInstallmentAmount;
        if (i === params.installment_count) {
            const previousTotal = new Decimal(baseInstallmentAmount).times(i - 1);
            installmentAmount = new Decimal(remainingBalance).minus(previousTotal).toDecimalPlaces(2).toNumber();
        }
        const installment = {
            seq: i,
            due_date: dueDate,
            amount_due: installmentAmount,
            amount_paid: 0,
            status: 'pending',
            penalty_accrued: 0,
        };
        // Calculate penalty if overdue
        installment.penalty_accrued = calculatePenalty(installment, penaltyRule, currentDate, timezone);
        // Determine status
        installment.status = determineInstallmentStatus(installment, params.grace_days, currentDate, timezone);
        // Update counters
        totalAmount = totalAmount.plus(installmentAmount);
        if (installment.status === 'overdue') {
            overdueCount++;
        }
        else if (installment.status === 'paid') {
            paidCount++;
        }
        // Set next due date (first pending installment)
        if (!nextDueDate && installment.status === 'pending') {
            nextDueDate = dueDate;
        }
        installments.push(installment);
    }
    return {
        installments,
        total_amount: totalAmount.toDecimalPlaces(2).toNumber(),
        remaining_balance: remainingBalance,
        next_due_date: nextDueDate,
        overdue_count: overdueCount,
        paid_count: paidCount,
    };
}
// Legacy function name for backward compatibility
export function generateInstallmentSchedule(params, currentDate = new Date()) {
    return generateInstallments(params, currentDate);
}
// Function to convert calculated installments to database records
export function createDatabaseInstallmentRecords(agreementId, calculatedInstallments) {
    return calculatedInstallments.map(installment => ({
        agreement_id: agreementId,
        seq: installment.seq,
        due_date: installment.due_date,
        amount_due: installment.amount_due,
        amount_paid: installment.amount_paid,
        status: installment.status,
        penalty_accrued: installment.penalty_accrued,
        paid_at: installment.paid_at,
    }));
}
// Function to validate that total installments equal the expected amount
export function validateInstallmentTotal(installments, expectedTotal) {
    const actualTotal = installments.reduce((sum, inst) => sum + inst.amount_due, 0);
    const difference = Math.abs(actualTotal - expectedTotal);
    // Allow for small rounding differences (up to 0.01 LYD)
    const valid = difference <= 0.01;
    return {
        valid,
        actualTotal: roundToTwoDecimals(actualTotal),
        difference: roundToTwoDecimals(difference),
    };
}
// Function to get timezone-aware current date for a shop
export function getCurrentDateForShop(timezone = DEFAULT_TIMEZONE) {
    const now = new Date();
    return fromZonedTime(now, timezone);
}
// Enhanced validation function with timezone support
export function validateAgreementParamsWithTimezone(params, timezone = DEFAULT_TIMEZONE) {
    const errors = [];
    if (params.total_price <= 0) {
        errors.push('Total price must be positive');
    }
    if (params.deposit < 0) {
        errors.push('Deposit cannot be negative');
    }
    if (params.deposit > params.total_price) {
        errors.push('Deposit cannot exceed total price');
    }
    if (params.admin_fee_total < 0) {
        errors.push('Admin fee cannot be negative');
    }
    if (!['weekly', 'bi-weekly', 'monthly'].includes(params.schedule_type)) {
        errors.push('Invalid schedule type');
    }
    if (params.installment_count < 1 || params.installment_count > 120) {
        errors.push('Installment count must be between 1 and 120');
    }
    if (params.due_day < 1 || params.due_day > 31) {
        errors.push('Due day must be between 1 and 31');
    }
    if (params.grace_days < 0 || params.grace_days > 30) {
        errors.push('Grace days must be between 0 and 30');
    }
    // Validate timezone
    try {
        // Test if the timezone is valid by trying to create a date in it
        const testDate = new Date();
        fromZonedTime(testDate, timezone);
    }
    catch (error) {
        errors.push(`Invalid timezone: ${timezone}`);
    }
    // Only validate start date if it's in the past relative to today (not test date)
    const today = getCurrentDateForShop(timezone);
    today.setHours(0, 0, 0, 0);
    const startDate = fromZonedTime(params.start_date, timezone);
    startDate.setHours(0, 0, 0, 0);
    if (startDate < today) {
        errors.push('Start date cannot be in the past');
    }
    return {
        valid: errors.length === 0,
        errors,
    };
}
// Function to update installment schedule with payments
export function updateInstallmentSchedule(schedule, paymentAmount, paymentDate = new Date()) {
    let remainingPayment = paymentAmount;
    const updatedInstallments = [...schedule.installments];
    // Sort installments by due date (oldest first)
    updatedInstallments.sort((a, b) => a.due_date.getTime() - b.due_date.getTime());
    for (const installment of updatedInstallments) {
        if (remainingPayment <= 0)
            break;
        const outstandingAmount = roundToTwoDecimals(installment.amount_due - installment.amount_paid);
        if (outstandingAmount > 0) {
            const paymentToApply = Math.min(remainingPayment, outstandingAmount);
            installment.amount_paid = roundToTwoDecimals(installment.amount_paid + paymentToApply);
            remainingPayment = roundToTwoDecimals(remainingPayment - paymentToApply);
            // Update status
            if (installment.amount_paid >= installment.amount_due) {
                installment.status = 'paid';
                installment.paid_at = paymentDate;
            }
            else if (installment.amount_paid > 0) {
                installment.status = 'partial';
            }
        }
    }
    // Recalculate schedule statistics
    const overdueCount = updatedInstallments.filter(i => i.status === 'overdue').length;
    const paidCount = updatedInstallments.filter(i => i.status === 'paid').length;
    const nextDueDate = updatedInstallments.find(i => i.status === 'pending')?.due_date;
    return {
        installments: updatedInstallments,
        total_amount: schedule.total_amount,
        remaining_balance: roundToTwoDecimals(schedule.remaining_balance - (paymentAmount - remainingPayment)),
        next_due_date: nextDueDate,
        overdue_count: overdueCount,
        paid_count: paidCount,
    };
}
// Function to format currency for display (renamed to avoid conflict)
export function formatCurrencyForDisplay(amount, currency = 'LYD') {
    return new Intl.NumberFormat('ar-LY', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}
// Function to format date for display (renamed to avoid conflict)
export function formatDateForDisplay(date, locale = 'ar-LY') {
    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(date);
}
// Export default penalty rule for new agreements
export const DEFAULT_PENALTY_RULE = {
    type: 'fixed_per_day',
    amount: 1, // 1 LYD per day overdue
    description: 'Fixed penalty of 1 LYD per day overdue',
};
export const DEFAULT_PENALTY_RULE_JSON = JSON.stringify(DEFAULT_PENALTY_RULE);
//# sourceMappingURL=installment-calculator.js.map